'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import {
  isAuthenticated,
  getUserRole,
  canAccessRoute,
  getDefaultRedirect,
  ROLES,
  type Role,
} from '@/lib/auth';
import Loading from '@/components/loading';
import RoleChangeRedirect from '@/components/RoleChangeRedirect';

interface RouteProtectionProps {
  children: React.ReactNode;
}

export default function RouteProtection({ children }: RouteProtectionProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkAccess = () => {
      // Early return if pathname is not available yet
      if (!pathname) {
        return;
      }

      // Public routes that don't require authentication
      const publicRoutes = [
        '/login',
        '/register',
        '/verify-otp',
        '/',
      ];

      const isPublicRoute = publicRoutes.some(
        (route) => pathname === route || pathname.startsWith(route + '/')
      );

      // If it's a public route, allow access
      if (isPublicRoute) {
        setIsAuthorized(true);
        setIsChecking(false);

        // If user is already authenticated and tries to access auth pages, redirect to their dashboard
        if (
          isAuthenticated() &&
          (pathname === '/login' ||
            pathname === '/register' ||
            pathname === '/verify-otp')
        ) {
          const userRole = getUserRole();
          const redirect = getDefaultRedirect(userRole);
          router.replace(redirect);
        }
        return;
      }

      // Check if user is authenticated
      if (!isAuthenticated()) {
        setIsAuthorized(false);
        setIsChecking(false);
        router.replace(`/login?redirect=${encodeURIComponent(pathname)}`);
        return;
      }

      // Check if user has permission to access the route
      if (!canAccessRoute(pathname)) {
        setIsAuthorized(false);
        setIsChecking(false);

        // Redirect to appropriate dashboard based on role
        const userRole = getUserRole();
        const redirect = getDefaultRedirect(userRole);
        router.replace(redirect);
        return;
      }

      // User is authenticated and has permission
      setIsAuthorized(true);
      setIsChecking(false);
    };

    checkAccess();
  }, [pathname, router]);

  // Show loading while checking permissions or if pathname is not available
  if (isChecking || !pathname) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size={60} />
      </div>
    );
  }

  // If not authorized, don't render children (will redirect)
  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size={60} />
      </div>
    );
  }

  // User is authorized, render children
  return (
    <>
      <RoleChangeRedirect />
      {children}
    </>
  );
}

// Higher-order component for specific role protection
export function withRoleProtection(
  Component: React.ComponentType,
  allowedRoles: Role[],
) {
  return function ProtectedComponent(props: any) {
    const router = useRouter();
    const pathname = usePathname();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
      // Early return if pathname is not available yet
      if (!pathname) {
        return;
      }

      const userRole = getUserRole();

      if (!isAuthenticated()) {
        router.replace(`/login?redirect=${encodeURIComponent(pathname)}`);
        return;
      }

      if (!userRole || !allowedRoles.includes(userRole)) {
        const redirect = getDefaultRedirect(userRole);
        router.replace(redirect);
        return;
      }

      setIsAuthorized(true);
      setIsChecking(false);
    }, [pathname, router]);

    if (isChecking || !pathname) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <Loading size={60} />
        </div>
      );
    }

    if (!isAuthorized) {
      return null;
    }

    return <Component {...props} />;
  };
}

// Component to conditionally render based on role
export function RoleGate({
  children,
  allowedRoles,
  fallback,
}: {
  children: React.ReactNode;
  allowedRoles: Role[];
  fallback?: React.ReactNode;
}) {
  const userRole = getUserRole();

  if (!userRole || !allowedRoles.includes(userRole)) {
    return <>{fallback || null}</>;
  }

  return <>{children}</>;
}

// Component to conditionally render based on authentication
export function AuthGate({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  if (!isAuthenticated()) {
    return <>{fallback || null}</>;
  }

  return <>{children}</>;
}